import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { ImageUpload } from '../../components/cms/ImageUpload';
import blogApiService from '../../services/blogApiService';
import type { BlogAuthor, AuthorCreateDTO } from '../../types/blog';
import { authorSchema } from '../../types/blog';
import { extractErrorMessage } from '../../utils/errorHandler';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';

interface AuthorEditorProps {
  isOpen: boolean;
  author: BlogAuthor | null;
  onClose: () => void;
  onSave: () => void;
}

const AuthorEditor: React.FC<AuthorEditorProps> = ({ isOpen, author, onClose, onSave }) => {
  const isEditing = !!author;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState('');
  const [bio, setBio] = useState('');
  const [position, setPosition] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setName(author?.name ?? '');
      setEmail(author?.email ?? '');
      setAvatar(author?.avatar ?? '');
      setBio(author?.bio ?? '');
      setPosition(author?.position ?? '');
      setErrors({});
    }
  }, [isOpen, author]);

  const handleSave = async () => {
    setErrors({});

    const validation = authorSchema.safeParse({ name, email, avatar, bio, position });

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((e) => {
        fieldErrors[e.path[0] as string] = e.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const dto: AuthorCreateDTO = { name, email, avatar, bio, position };

    try {
      setIsSaving(true);
      if (isEditing) {
        await blogApiService.updateAuthor(author.id, dto);
        toast.success('Author updated successfully');
      } else {
        await blogApiService.createAuthor(dto);
        toast.success('Author created successfully');
      }
      onSave();
    } catch (error) {
      toast.error(extractErrorMessage(error));
      logger.error('Failed to save author:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isEditing ? 'Edit Author' : 'New Author'}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {isEditing ? 'Update the author details below.' : 'Fill in the details to create a new author.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Avatar */}
          <div className="flex justify-center">
            <div className="w-24">
              <ImageUpload
                value={avatar}
                onChange={setAvatar}
                onRemove={() => setAvatar('')}
                label="Avatar"
                aspectRatio="1/1"
              />
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className={errors.name ? 'border-red-400' : ''}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Email</label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              type="email"
              className={errors.email ? 'border-red-400' : ''}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          {/* Position */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Position</label>
            <Input
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="e.g., Editor, Writer, Chef"
              className={errors.position ? 'border-red-400' : ''}
            />
            {errors.position && <p className="text-xs text-red-500 mt-1">{errors.position}</p>}
          </div>

          {/* Bio */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Short biography..."
              className="w-full min-h-[80px] p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-slate-400"
              maxLength={1000}
            />
            <p className="text-xs text-slate-400 text-right mt-0.5">{bio.length}/1000</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} size="sm">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size="sm"
              className="bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 text-white dark:text-slate-900"
            >
              {isSaving ? (
                <>
                  <Loader className="h-4 w-4 mr-1.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>{isEditing ? 'Update' : 'Create'} Author</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthorEditor;
